import React, { useEffect, useState, useContext } from "react";
import { getTenants } from "../api/client";
import AuthContext from "../AuthContext";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import Chart from "react-apexcharts";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
// ViewModeToggle removed from this page

ChartJS.register(ArcElement, Tooltip, Legend);

const chartPalette = ['#8A17D1', '#D326AD', '#B06BE6', '#E76BC7', '#6C2FA1'];
const chartText = '#E6EEF3';
const chartGrid = 'rgba(199, 210, 254, 0.16)';

const normalizePlanName = (plan) => {
    const name = String(plan || '').trim();
    if (!name || name.toLowerCase() === 'free') return 'Basic';
    return name;
};

function DashboardSpinner() {
    return (
        <div className="flex flex-col items-center justify-center gap-4">
            <div
                className="h-12 w-12 animate-spin rounded-full border-4 border-gray-300"
                style={{ borderTopColor: '#8A17D1' }}
                aria-label="Loading dashboard"
            />
            <span className="text-sm text-gray-500">Loading dashboard...</span>
        </div>
    );
}

function ValueSpinner() {
    return (
        <div
            className="h-7 w-7 animate-spin rounded-full border-4 border-gray-300"
            style={{ borderTopColor: '#8A17D1' }}
            aria-label="Loading value"
        />
    );
}

// ViewModeToggle removed from this page
export default function SuperadminDashboard() {
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const authContext = useContext(AuthContext);
    // const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

    useEffect(() => {
        let mounted = true;
        async function load() {
            try {
                const items = await getTenants();
                if (mounted) setTenants(items || []);
            } catch (e) {
                setError(e?.message || "Network error");
            } finally {
                if (mounted) setLoading(false); 
            }
        }
        load();
        return () => { mounted = false; };
    }, []);

    // no view mode state — keep original dashboard presentation

    // mobile sidebar handled locally in page; no global event

    if (error) return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-xl text-red-600">{error}</div>
        </div>
    );

    const totalTenants = tenants.length;
    const totalUsers = tenants.reduce((s, t) => s + (t.userCount || t.UserCount || 0), 0);
    const totalRevenue = tenants.reduce((s, t) => s + (t.salesTotal || t.SalesTotal || 0), 0);
    const activeTenants = tenants.filter(t => (t.isActive ?? t.IsActive ?? true)).length;

    // Prepare chart data: distribution by subscription plan
    const planMap = {};
    tenants.forEach(t => {
        const plan = normalizePlanName(t.subscriptionPlan || t.SubscriptionPlan);
        planMap[plan] = (planMap[plan] || 0) + 1;
    });
    const planLabels = Object.keys(planMap);
    const planValues = Object.values(planMap);

    const doughnutData = {
        labels: planLabels,
        datasets: [
            {
                data: planValues,
                backgroundColor: chartPalette,
                borderColor: '#0B1220',
                borderWidth: 3,
            },
        ],
    };

    const apexOptions = {
        chart: { id: 'tenants-by-plan', toolbar: { show: false }, foreColor: chartText },
        colors: ['#8A17D1'],
        plotOptions: { bar: { borderRadius: 5, columnWidth: '55%' } },
        dataLabels: { enabled: false },
        grid: { borderColor: chartGrid },
        xaxis: {
            categories: planLabels,
            labels: { style: { colors: chartText } },
            axisBorder: { color: chartGrid },
            axisTicks: { color: chartGrid },
        },
        yaxis: { labels: { style: { colors: chartText } } },
        tooltip: { theme: 'dark' },
    };
    const apexSeries = [{ name: 'Tenants', data: planValues }];
    const doughnutOptions = {
        plugins: {
            legend: { labels: { color: chartText, padding: 16 } },
            tooltip: { backgroundColor: '#071026', titleColor: '#FFFFFF', bodyColor: chartText },
        },
    };

    return (
        <>
        <Header />
        <div className="min-h-screen bg-gray-50">
            {/* Mobile sidebar is opened via header hamburger */}
            <div className="grid grid-cols-12">
                <aside className="col-span-2 hidden lg:flex">
                    <Sidebar />
                </aside>

                {/* Mobile sidebar handled by header Disclosure on small screens */}

                <main className="col-span-12 lg:col-span-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8">
                    <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                            <h1 className="text-3xl font-extrabold text-gray-900">Superadmin Dashboard</h1>
                            <p className="mt-2 text-sm text-gray-500">Overview of tenant activity and usage.</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => authContext.signout()}
                                className="px-4 py-2 bg-red-600 text-white rounded-md shadow-sm text-sm hover:bg-red-700"
                            >
                                Logout
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        <Card title="Total Tenants" value={loading ? <ValueSpinner /> : totalTenants} variant="indigo" />
                        <Card title="Total Users" value={loading ? <ValueSpinner /> : totalUsers} variant="teal" />
                        <Card title="Total Revenue" value={loading ? <ValueSpinner /> : `₦${(Number(totalRevenue) || 0).toLocaleString()}`} variant="green" />
                        <Card title="Active Tenants" value={loading ? <ValueSpinner /> : activeTenants} variant="yellow" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white p-6 rounded-lg shadow">
                            <h3 className="text-lg font-medium mb-4">Tenants by Subscription Plan</h3>
                            {loading ? (
                                <div className="flex h-[300px] items-center justify-center"><DashboardSpinner /></div>
                            ) : (
                                <Chart options={apexOptions} series={apexSeries} type="bar" height={300} width="100%" />
                            )}
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow">
                            <h3 className="text-lg font-medium mb-4">Subscription Distribution</h3>
                            {loading ? (
                                <div className="flex h-[300px] items-center justify-center"><DashboardSpinner /></div>
                            ) : (
                                <Doughnut data={doughnutData} options={doughnutOptions} />
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </div>
        </>
    );
}

// StatCard removed — restoring original compact Card layout

function Card({ title, value, variant = 'indigo' }) {
    const variants = {
        indigo: { bg: 'bg-indigo-50', icon: (
            <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 21h18V3H3v18zM7 7h4v4H7V7zM13 7h4v4h-4V7zM7 13h4v4H7v-4z" />
            </svg>
        ) },
        teal: { bg: 'bg-teal-50', icon: (
            <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20v-2a4 4 0 00-3-3.87M9 20v-2a4 4 0 013-3.87M12 7a4 4 0 110 8 4 4 0 010-8z" />
            </svg>
        ) },
        green: { bg: 'bg-emerald-50', icon: (
            <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.1 0-2 .9-2 2h4c0-1.1-.9-2-2-2zm0 8c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2z" />
            </svg>
        ) },
        yellow: { bg: 'bg-yellow-50', icon: (
            <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
        ) },
    };
    const v = variants[variant] || variants.indigo;
    return (
        <article className="relative flex items-center gap-4 rounded-lg border border-gray-100 bg-white p-4 shadow-sm">
            <div className={`flex-shrink-0 h-12 w-12 rounded-md ${v.bg} flex items-center justify-center`}>
                {v.icon}
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-500 truncate">{title}</div>
                <div className="mt-1 text-2xl font-semibold text-gray-900 truncate">{value}</div>
            </div>
            <div className="ml-3 text-xs text-gray-400">&nbsp;</div>
        </article>
    );
}
